import { Fragment, useRef, useState } from "react";
import { css } from "@emotion/react";

import useForm from "../../../../hooks/useForm";
import { AUTH_URL, getUrl } from "../../../../includes/routes";
import Button from "../../../Button";
import Form from "../../../form/Form";
import Input from "../../../form/Input";
import FormActions from "../../../form/FormActions";
import { useZitadel, ZitadelSession } from "../../../../contexts/ZitadelContext";
import { useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { FAILED_AUTHENTICATION_PARAM, zitadelGraphqlErrors } from "../../../../includes/constants";
import { API_BASE_URL } from "../../../../../config/config";

type ZitadelSendOtpEmailCodeMutationResponse = {
    zitadelSendOtpEmailCode: ZitadelSession;
}

type ZitadelSendOtpEmailCodeMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

type ZitadelCancelTotpRegistrationMutationResponse = {
    zitadelCancelOtpEmailRegistration: ZitadelSession;
}

type ZitadelCancelTotpRegistrationMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

type ZitadelCompleteOtpEmailChallengeMutationResponse = {
    zitadelCompleteOtpEmailChallenge: {
        id: string
    };
}

type ZitadelCompleteOtpEmailChallengeMutationVariables = {
    sessionId: string;
    sessionToken: string;
    code: string;
}

const OtpEmailChallenge = () => {
    const navigate = useNavigate();

    const errorElementRef = useRef<HTMLParagraphElement>(null);
    const [isOtpEmailLoading, setIsOtpEmailLoading] = useState(false);
    const [isSendOtpEmailLoading, setIsSendOtpEmailLoading] = useState(false);
    const [isCancelLoading, setIsCancelLoading] = useState(false);
    const [isCompletionNoticeVisible, setIsCompletionNoticeVisible] = useState(false);

    const {
        loginInfo,
        clearLoginInfo,
        session,
        setSession,
        clearSession,
    } = useZitadel();

    const { formState, onFieldChange, onFieldInvalid, formErrors, setFormErrors } = useForm({
        fields: { code: "" },
    });

    const [zitadelSendOtpEmailCode] =
        useMutation<ZitadelSendOtpEmailCodeMutationResponse, ZitadelSendOtpEmailCodeMutationVariables>(
            ZITADEL_SEND_OTP_EMAIL_CODE,
        );

    const [zitadelCancelOtpEmailRegistration] =
        useMutation<ZitadelCancelTotpRegistrationMutationResponse, ZitadelCancelTotpRegistrationMutationVariables>(
            ZITADEL_CANCEL_OTP_EMAIL_REGISTRATION,
        );

    const [zitadelCompleteOtpEmailChallenge] =
        useMutation<ZitadelCompleteOtpEmailChallengeMutationResponse, ZitadelCompleteOtpEmailChallengeMutationVariables>(
            ZITADEL_COMPLETE_OTP_EMAIL_CHALLENGE,
        );

    const onSubmit = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register OTP email.");

        setIsOtpEmailLoading(true);

        zitadelCompleteOtpEmailChallenge({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
                code: formState.code,
            },
            fetchPolicy: 'network-only',
        }).then(() => {
            if (!loginInfo?.isRedirectDisabled) {
                clearLoginInfo();
                clearSession();

                let url = API_BASE_URL + "/auth/login-redirect";
                if (loginInfo?.backUrl) url += "?BackURL=" + loginInfo.backUrl;
                window.location.href = url;
            } else {
                setIsCompletionNoticeVisible(true);
            }
        }).catch((e) => {
            const graphQlErrors = e.graphQLErrors;
            if (graphQlErrors) {
                if (
                    graphQlErrors.find(
                        (graphQlError: any) => graphQlError.extensions?.details?.includes(
                            zitadelGraphqlErrors.INVALID_MFA_CODE
                        )
                    )
                ) {
                    setFormErrors({ code: "Invalid code" });
                    // Screen reader announces a field error message.
                    if (errorElementRef.current) errorElementRef.current.focus();
                    setIsOtpEmailLoading(false);
                    return;
                }
            }
            clearLoginInfo();
            clearSession();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    };

    const onCancel = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to cancel OTP email registration.");

        setIsCancelLoading(true);

        zitadelCancelOtpEmailRegistration({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
            },
            fetchPolicy: 'network-only',
        }).then(({ data }) => {
            if (!data) throw new Error("OTP email registration cancellation did not return a valid response.");

            const session = data.zitadelCancelOtpEmailRegistration;

            setSession(session);
        }).catch(() => {
            clearLoginInfo();
            clearSession();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }

    const onSendOtpEmail = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register email authentication.");

        setIsSendOtpEmailLoading(true);

        zitadelSendOtpEmailCode({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
            },
            fetchPolicy: 'network-only',
        }).then(({ data }) => {
            if (!data) throw new Error("OTP email registration did not return a valid response.");

            setIsSendOtpEmailLoading(false);
            setSession(data.zitadelSendOtpEmailCode);
        }).catch(() => {
            clearLoginInfo();
            clearSession();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    };

    const isActionsDisabled = isOtpEmailLoading || isSendOtpEmailLoading || isCancelLoading;

    /** Completion notice overrides all */
    if (isCompletionNoticeVisible) return <div>Authentication complete, you may now close this window</div>;

    if (!loginInfo || !session) return null;

    const isOneMfaMethodAllowed = session.metadata.isTotpAllowed !== session.metadata.isOtpEmailAllowed;
    const isOneMfaMethodForced = isOneMfaMethodAllowed && !session?.metadata.isSkipAllowed;

    return (
        <div css={rootStyle}>
            <h1>Multi-Factor Authentication</h1>
            {session.metadata.isMfaRegistrationRequired ? (
                <Fragment>
                    <p>Each time you log in, you will receive a verification code via email.</p>
                    <hr />
                </Fragment>
            ) : (
                <Fragment>
                    <p>You have chosen to receive a verification code via email.</p>
                </Fragment>
            )}
            <p>
                Your verification code has been sent to
                <br /><em>{session.metadata.userEmail}</em>
            </p>
            <p>Please check your inbox and enter the verification code below</p>
            <Form onSubmit={onSubmit}>
                <div css={groupingStyle}>
                    <h2>Verification code</h2>
                    <Input
                        hasError={!!formErrors.code}
                        label="Enter the code from your email"
                        name="code"
                        onChange={onFieldChange}
                        onInvalid={onFieldInvalid}
                        value={formState.code}
                        aria-label={"Email verification code"}
                        hasBorder
                        required
                    />
                    <p css={errorStyle} ref={errorElementRef} tabIndex={-1} role={"alert"}>{formErrors?.code}</p>
                    <p>
                        Didn't receive code?&nbsp;
                        <button css={resendEmailButtonStyle} onClick={onSendOtpEmail} disabled={isActionsDisabled}>
                            Resend
                        </button>
                    </p>
                    <div css={formActionsStyle}>
                        <FormActions customCss={customFormActionsStyles}>
                            {session.metadata.isMfaRegistrationRequired && !isOneMfaMethodForced && (
                                <Button
                                    css={cancelButtonStyle}
                                    type="button"
                                    onClick={onCancel}
                                    disabled={isActionsDisabled}
                                    forceLoading={isCancelLoading}
                                    useOutline
                                >
                                    Choose another method
                                </Button>
                            )}
                            <Button
                                css={buttonStyle}
                                type="submit"
                                disabled={isActionsDisabled}
                                forceLoading={isOtpEmailLoading}
                                useOutline
                            >
                                Confirm
                            </Button>
                        </FormActions>
                    </div>
                </div>
            </Form>
        </div>
    );
}

const ZITADEL_SEND_OTP_EMAIL_CODE = gql`
    mutation zitadelSendOtpEmailCode($sessionId: String!, $sessionToken: String!) {
        zitadelSendOtpEmailCode(sessionId: $sessionId, sessionToken: $sessionToken) {
            id
            token
            metadata {
                userEmail
                isMfaRegistrationRequired
                isTotpChallengeRequired
                isOtpEmailChallengeRequired
                isTotpAllowed
                isOtpEmailAllowed
                isSkipAllowed
            }
        }
    }
`;

const ZITADEL_CANCEL_OTP_EMAIL_REGISTRATION = gql`
    mutation zitadelCancelOtpEmailRegistration($sessionId: String!, $sessionToken: String!) {
        zitadelCancelOtpEmailRegistration(sessionId: $sessionId, sessionToken: $sessionToken) {
            id
            token
            metadata {
                userEmail
                isMfaRegistrationRequired
                isTotpChallengeRequired
                isOtpEmailChallengeRequired
                isTotpAllowed
                isOtpEmailAllowed
                isSkipAllowed
            }
        }
    }
`;

const ZITADEL_COMPLETE_OTP_EMAIL_CHALLENGE = gql`
    mutation zitadelCompleteOtpEmailChallenge($sessionId: String!, $sessionToken: String!, $code: String!) {
        zitadelCompleteOtpEmailChallenge(sessionId: $sessionId, sessionToken: $sessionToken, code: $code) {
            id
        }
    }
`;

const rootStyle = (theme: EmotionTheme) => css`
    /** Heavily derived from SignIn.tsx::authContentStyle */
    margin-top: 40px;
    width: 510px;
    position: relative;
    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }
    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }

    /** Heavily derived from SignIn.tsx::leftHeaderStyle */
    h1 {
        margin: 0;
        color: var(--primary-colour);
        font-weight: ${theme.fonts.weights.bold};

        ${theme.breakpoints.up("xxl")} {
            font-size: 50px;
            line-height: 75px;
        }

        ${theme.breakpoints.between("md", "xl")} {
            font-size: 43px;
            line-height: 65px;
        }

        ${theme.breakpoints.down("sm")} {
            font-size: 30px;
            line-height: 45px;
        }
    }

    h2 {
        margin: 0;
    }

    p {
        margin:  0;
    }

    hr {
        margin: 0;
        width: 100%;
    }

    em {
        color: var(--primary-colour);
        font-weight: 400;
        font-style: normal;
    }

    --primary-colour: ${theme.colours.curiousBlue};

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2rem;
    padding-bottom: 10rem;
`;

const groupingStyle = css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

/** Heavily derived from SignIn.tsx::formActionsStyle */
const formActionsStyle = (theme: EmotionTheme) => css`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    ${theme.breakpoints.up('sm')} {
        justify-content: center;
    }
`;

/** Heavily derived from SignIn.tsx::customFormActionsStyles */
const customFormActionsStyles = css`
    width: 100%;
    justify-content: center;
`;

/** Heavily derived from SignIn.tsx::buttonStyle */
const buttonStyle = (theme: EmotionTheme) => css`
    background-color: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;
const cancelButtonStyle = (theme: EmotionTheme) => css`
    ${buttonStyle(theme)};
    background: none;
    color: ${theme.colours.blue['400']};
    border: 1px solid ${theme.colours.blue['400']};
`;

const resendEmailButtonStyle = () => css`
    padding: 0;
    margin: 0;
    border: none;
    cursor: pointer;
    background: none;
    font-weight: 400;
    text-decoration: underline;
    color: var(--primary-colour);
`;

const errorStyle = (theme: EmotionTheme) => css`
    color: ${theme.colours.red[500]};
`;

export default OtpEmailChallenge;
